import { useMemo, useState } from "react";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { View } from "@common/types/views/views";
import type { ApplyCloseTemplateEntitySelectionRow } from "./applyCloseTemplateEntitySelection.types";
import {
    entityMatchesFilters,
    workspaceMatchesFilters,
} from "./applyCloseTemplateEntityFilters";
import { defaultApplyCloseEntitySelectionView } from "./applyCloseTemplateEntitySelectionView.constants";

export function useApplyCloseTemplateEntityFilters(
    workspaces: WorkspaceSchema[] | undefined,
) {
    const [search, setSearch] = useState("");
    const [selectedPlans, setSelectedPlans] = useState<FLAT_RATE_PLANS[]>([]);
    const [selectedIndustries, setSelectedIndustries] = useState<
        EntityProfileIndustry[]
    >([]);
    const [selectedLegalTypes, setSelectedLegalTypes] = useState<
        EntityProfileType[]
    >([]);
    const [view, setView] = useState<View>(
        defaultApplyCloseEntitySelectionView,
    );

    const tableData = useMemo<ApplyCloseTemplateEntitySelectionRow[]>(() => {
        if (!workspaces?.length) {
            return [];
        }
        const rows: ApplyCloseTemplateEntitySelectionRow[] = [];
        for (const ws of workspaces) {
            const entities = (ws.entities ?? []).filter(
                (e) => !e.isPersonal && !e.isMockEntity,
            );
            if (entities.length === 0) {
                continue;
            }
            rows.push({
                id: `ws-${ws.id}`,
                isClientRow: true,
                workspaceId: ws.id,
                clientLabel: ws.name,
                name: ws.name,
                iconColor: "",
                logo: null,
                startDate: null,
                endDate: null,
                workStartDate: null,
                workDeliveryDate: null,
                preparerId: null,
                reviewerId: null,
                managerId: null,
                industries: [],
                legalType: null,
                plan: ws.plan,
                children: entities.map((entity) => ({
                    id: String(entity.id),
                    isClientRow: false,
                    workspaceId: ws.id,
                    clientLabel: ws.name,
                    entityIdStr: String(entity.id),
                    entityUuid: entity.uuid,
                    name: entity.name ?? String(entity.id),
                    iconColor: entity.iconColor ?? "",
                    logo: entity.logo ?? null,
                    startDate: null,
                    endDate: null,
                    workStartDate: null,
                    workDeliveryDate: null,
                    preparerId: null,
                    reviewerId: null,
                    managerId: null,
                    industries: entity.industries,
                    legalType: entity.legalType,
                })),
            });
        }
        return rows;
    }, [workspaces]);

    const filteredTableData =
        useMemo((): ApplyCloseTemplateEntitySelectionRow[] => {
            const lowerSearch = search.toLowerCase();
            const result: ApplyCloseTemplateEntitySelectionRow[] = [];
            for (const wsRow of tableData) {
                if (
                    !workspaceMatchesFilters(wsRow, {
                        selectedPlans,
                    })
                ) {
                    continue;
                }
                const children = (wsRow.children ?? []).filter((entity) =>
                    entityMatchesFilters(entity, wsRow.name, {
                        lowerSearch,
                        selectedIndustries,
                        selectedLegalTypes,
                    }),
                );
                if (children.length > 0) {
                    result.push({ ...wsRow, children });
                }
            }
            return result;
        }, [
            tableData,
            search,
            selectedPlans,
            selectedIndustries,
            selectedLegalTypes,
        ]);

    const filteredEntityIds = useMemo(
        () =>
            new Set(
                filteredTableData.flatMap(
                    (wsRow) => wsRow.children?.map((c) => c.id) ?? [],
                ),
            ),
        [filteredTableData],
    );

    return {
        search,
        setSearch,
        selectedPlans,
        setSelectedPlans,
        selectedIndustries,
        setSelectedIndustries,
        selectedLegalTypes,
        setSelectedLegalTypes,
        view,
        setView,
        filteredTableData,
        filteredEntityIds,
    };
}
